import { createContext, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AutoContextCollector } from "@/client/lib/agent-context/auto-collect";
import { buildContextDocument } from "@/client/lib/agent-context/build-context-doc";
import type { ContextEntry } from "@/client/lib/agent-context/registry";
import { getAll, subscribe } from "@/client/lib/agent-context/registry";
import { api } from "@/client/lib/api";
import type { AgentStreamEvent, ClientAgentMessage, PageContext, ProposalStatus } from "@/types/agent";
import { type ConversationSummary, useConversations } from "./agent-conversations";
import { DAILY_CAP_USD, loadDailyCost, saveDailyCost } from "./agent-cost-storage";
import { handleSseEvent } from "./agent-sse-reducer";

export type { ConversationSummary };

export interface AgentContextValue {
  messages: ClientAgentMessage[];
  isOpen: boolean;
  isStreaming: boolean;
  setIsOpen: (open: boolean) => void;
  toggle: () => void;
  sendMessage: (text: string) => Promise<void>;
  approveProposal: (proposalId: string) => void;
  rejectProposal: (proposalId: string) => void;
  clearChat: () => void;
  /** Per-turn token/cost metrics from the last done event */
  lastTokensIn: number;
  lastTokensOut: number;
  lastCostUsd: number;
  /** Accumulated cost for today (persisted to localStorage) */
  dailyCostUsd: number;
  dailyCapUsd: number;
  /** All registered context entries (for transparency page) */
  contextEntries: ContextEntry[];
  /** When true, contextDoc is NOT sent; dailyCapUsd drops to 0 */
  quietMode: boolean;
  setQuietMode: (quiet: boolean) => void;
  // ── Conversation persistence ────────────────────────────
  /** Top 20 non-archived conversations for the current user */
  conversations: ConversationSummary[];
  /** ID of the conversation currently in progress */
  currentConversationId: string | null;
  /** Load a past conversation's messages into state */
  loadConversation: (id: string) => Promise<void>;
  /** Archive a conversation */
  archiveConversation: (id: string) => Promise<void>;
}

export const AgentContext = createContext<AgentContextValue | null>(null);

let messageCounter = 0;
function nextId(): string {
  messageCounter += 1;
  return `msg-${messageCounter}-${Date.now()}`;
}

function extractRecordId(path: string): number | undefined {
  const match = path.match(/^\/insurance\/(\d+)/);
  return match ? Number(match[1]) : undefined;
}

export function AgentProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [messages, setMessages] = useState<ClientAgentMessage[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [lastTokensIn, setLastTokensIn] = useState(0);
  const [lastTokensOut, setLastTokensOut] = useState(0);
  const [lastCostUsd, setLastCostUsd] = useState(0);
  const [dailyCostUsd, setDailyCostUsd] = useState<number>(loadDailyCost);
  const [contextEntries, setContextEntries] = useState<ContextEntry[]>(getAll);
  const [quietMode, setQuietMode] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const currentConvIdRef = useRef<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { conversations, fetchConversation, archive } = useConversations(nextId);

  const dailyCapUsd = quietMode ? 0 : DAILY_CAP_USD;

  // Keep contextEntries in sync with the registry
  useEffect(() => {
    const unsub = subscribe((entries) => {
      setContextEntries(entries);
    });
    return unsub;
  }, []);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const updateLastAssistant = useCallback((updater: (msg: ClientAgentMessage) => ClientAgentMessage) => {
    setMessages((prev) => {
      const copy = [...prev];
      for (let i = copy.length - 1; i >= 0; i--) {
        if (copy[i]?.role === "assistant") {
          copy[i] = updater(copy[i] as ClientAgentMessage);
          break;
        }
      }
      return copy;
    });
  }, []);

  const handleUiCommand = useCallback(
    (e: AgentStreamEvent) => {
      if (e.type !== "ui_command") return;
      switch (e.command) {
        case "navigate":
          if (e.payload.path) navigate(e.payload.path);
          break;
        case "download":
          if (e.payload.url) window.open(e.payload.url, "_blank");
          break;
        case "open_form":
        case "prefill_form":
          navigate("/insurance/new", { state: { prefill: e.payload.fields } });
          break;
        case "refresh":
          window.location.reload();
          break;
      }
    },
    [navigate],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      if (isStreaming) return;
      if (dailyCapUsd > 0 && dailyCostUsd >= dailyCapUsd) return;

      const userMsg: ClientAgentMessage = { id: nextId(), role: "user", content: text, timestamp: new Date() };
      const assistantMsg: ClientAgentMessage = {
        id: nextId(),
        role: "assistant",
        content: "",
        toolCalls: [],
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);

      const abortController = new AbortController();
      abortRef.current = abortController;

      const apiMessages = [...messages, userMsg].map((m) => ({ role: m.role, content: m.content }));
      const contextDoc = quietMode ? undefined : buildContextDocument();
      const pageContext: PageContext = {
        path: location.pathname,
        recordId: extractRecordId(location.pathname),
        contextDoc,
      };

      try {
        await api.streamPost(
          "/api/agent/chat",
          { messages: apiMessages, pageContext, conversationId: currentConvIdRef.current ?? undefined },
          (event) => {
            const e = event as unknown as AgentStreamEvent;
            handleSseEvent(e, {
              updateLastAssistant,
              setConversationId: (id) => {
                currentConvIdRef.current = id;
                setCurrentConversationId(id);
              },
              onUiCommand: handleUiCommand,
              onDone: (usage) => {
                const turnCost = usage.costUsd ?? 0;
                setLastTokensIn(usage.inputTokens);
                setLastTokensOut(usage.outputTokens);
                setLastCostUsd(turnCost);
                setDailyCostUsd((prev) => {
                  const next = prev + turnCost;
                  saveDailyCost(next);
                  return next;
                });
              },
            });
          },
          abortController.signal,
        );
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          updateLastAssistant((msg) => ({
            ...msg,
            content: msg.content || "Failed to connect to the assistant. Please try again.",
          }));
        }
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [
      messages,
      isStreaming,
      dailyCostUsd,
      dailyCapUsd,
      quietMode,
      updateLastAssistant,
      handleUiCommand,
      location.pathname,
    ],
  );

  const approveProposal = useCallback(
    (proposalId: string) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.proposal?.proposalId === proposalId
            ? { ...msg, proposal: { ...msg.proposal, status: "approved" as ProposalStatus } }
            : msg,
        ),
      );
      void sendMessage(`I approve the proposed changes. Execute proposal ${proposalId}.`);
    },
    [sendMessage],
  );

  const rejectProposal = useCallback((proposalId: string) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.proposal?.proposalId === proposalId
          ? { ...msg, proposal: { ...msg.proposal, status: "rejected" as ProposalStatus } }
          : msg,
      ),
    );
  }, []);

  const clearChat = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([]);
    setIsStreaming(false);
    currentConvIdRef.current = null;
    setCurrentConversationId(null);
  }, []);

  const loadConversationById = useCallback(
    async (id: string) => {
      const loaded = await fetchConversation(id);
      setMessages(loaded);
      currentConvIdRef.current = id;
      setCurrentConversationId(id);
    },
    [fetchConversation],
  );

  const archiveConversationById = useCallback(
    async (id: string) => {
      await archive(id);
      if (currentConvIdRef.current === id) {
        currentConvIdRef.current = null;
        setCurrentConversationId(null);
      }
    },
    [archive],
  );

  return (
    <AgentContext.Provider
      value={{
        messages,
        isOpen,
        isStreaming,
        setIsOpen,
        toggle,
        sendMessage,
        approveProposal,
        rejectProposal,
        clearChat,
        lastTokensIn,
        lastTokensOut,
        lastCostUsd,
        dailyCostUsd,
        dailyCapUsd,
        contextEntries,
        quietMode,
        setQuietMode,
        conversations,
        currentConversationId,
        loadConversation: loadConversationById,
        archiveConversation: archiveConversationById,
      }}
    >
      <AutoContextCollector />
      {children}
    </AgentContext.Provider>
  );
}
